import axios from "axios";
import { toast } from "sonner";
import { pedirContrasenaPdf } from "@/components/ContrasenaPdf";
import { API_BASE } from "@/lib/dominio";

const API = API_BASE;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("roge_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const d = err.response?.data?.detail;
    const url = err.config?.url || "";
    const sesionCerrada = err.response?.status === 401 && localStorage.getItem("roge_token") && !url.includes("/auth/");
    if ((err.response?.status === 403 && (d?.code === "empleado_retirado" || typeof d === "string" && d.includes("desactivada"))) || sesionCerrada) {
      localStorage.removeItem("roge_token");
      if (window.location.pathname !== "/login") {
        sessionStorage.setItem("roge_aviso_login", typeof d === "string" ? d : d?.msg || "Tu sesión se cerró. Ingresa de nuevo.");
        window.location.assign("/login");
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Ocurrió un error. Intente nuevamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const sinCache = (url) => `${url}${url.includes("?") ? "&" : "?"}_ts=${Date.now()}`;

export const AVISO_PROTEGIDO = "El documento está protegido con contraseña: el archivo descargado pedirá la contraseña para abrirse.";

export async function downloadFile(url, filename) {
  const res = await api.get(sinCache(url), { responseType: "blob", headers: { "Cache-Control": "no-cache" } });
  const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement("a");
  link.href = blobUrl;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
  if (res.headers["x-documento-protegido"]) toast.warning(AVISO_PROTEGIDO);
  return res;
}

// Sube un archivo; si es un PDF protegido pide la contraseña antes de completar la carga.
export async function subirArchivo(url, fd) {
  const archivo = fd.get("file")?.name;
  for (;;) {
    try {
      return await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status !== 422 || !["pdf_protegido", "pdf_contrasena_incorrecta"].includes(d?.code)) throw err;
      const pwd = await pedirContrasenaPdf({ mensaje: d.msg, archivo, error: d.code === "pdf_contrasena_incorrecta" });
      if (!pwd) {
        const e = new Error("cancelado");
        e.response = { data: { detail: "Carga no completada: el PDF está protegido. Ingresa la contraseña o carga un archivo sin protección." } };
        throw e;
      }
      fd.set("pdf_password", pwd);
    }
  }
}

export const PERMS = {
  admin: ["ver", "editar", "crear", "importar", "exportar", "eliminar", "config", "usuarios", "usuarios_empleados", "empleados", "expedientes", "contrataciones", "plantillas", "certificados", "reglamento"],
  gestor: ["ver", "editar", "crear", "importar", "exportar", "empleados", "expedientes", "contrataciones", "plantillas", "certificados", "reglamento", "usuarios_empleados"],
  importador: ["ver", "importar", "exportar"],
  empresa_cliente: ["cliente"],
  visor: ["ver", "exportar", "empleados", "expedientes", "contrataciones", "certificados", "reglamento", "visor"],
};

export function can(roleOrUser, action) {
  // Prefer the server-provided permission set (source of truth); fall back to the role map.
  if (roleOrUser && typeof roleOrUser === "object") {
    if (Array.isArray(roleOrUser.permissions)) return roleOrUser.permissions.includes(action);
    return (PERMS[roleOrUser.role] || []).includes(action);
  }
  return (PERMS[roleOrUser] || []).includes(action);
}
